import { Box, Stack, Typography, styled, Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import Chart, { useChart } from "src/components/chart";
import { t } from "i18next";
import { formatNumber } from "src/utils/format-number";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

export default function ChartSearchesVisibility({ monthlyData, currency, lang }: { monthlyData: any[], currency: string, lang: string }) {
    const categories = monthlyData.map(item => item.month);
    const total_searches = monthlyData.map(item => item.total_searches);

    const chartOptions = useChart({
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { width: 2, curve: 'smooth' },
        fill: { type: 'solid' },
        colors: ['rgba(238, 122, 99, 1)', 'rgba(192, 153, 16, 1)'],
        xaxis: {
            tooltip: { enabled: false },
            type: 'category', categories, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false }
        },
        yaxis: [
            { show: false, labels: { formatter: (v: number) => formatNumber(v) }, min: 0 },
        ],
        grid: { show: false },
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const item = monthlyData[dataPointIndex] || { total_searches: 0, total_spend: 0 };

                return `
                            <div class="visibility-chart-tooltip" style="width: 250px">
                                <div class="visibility-chart-tooltip-header">
                                    <span class="visibility-chart-tooltip-title">${t("dashboard.global.brandPulse.searches")}</span>
                                    <span class="visibility-chart-tooltip-date">${item.month}</span>
                                </div>
                                <div class="visibility-chart-tooltip-row">
                                    <div class="visibility-chart-tooltip-indicator" style="background-color: rgba(238, 122, 99, 1)"></div>
                                    <span class="visibility-chart-tooltip-label">${t("dashboard.global.brandPulse.searches")}</span>
                                    <span style="color: rgba(51, 60, 71, 1)">${formatNumber(item.total_searches)}</span>
                                </div>
                            </div>
                        `;
            },
        },
        legend: { show: false },
    });

    const series = [
        { name: t("dashboard.global.brandPulse.searches"), data: total_searches },
    ];

    const renderFooter = (title: string) => {
        return (
            <Stack
                width="100%"
                sx={{
                    display: "flex",
                    height: 32,
                    px: "12px",
                    py: "8px",
                    alignItems: "flex-start",
                    alignSelf: "stretch",
                    backgroundColor: "rgba(145, 158, 171, 0.08)",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                }}
            >
                <Typography
                    fontSize="13px"
                    variant="caption"
                    color="rgba(99, 115, 129, 1)"
                >
                    {title}
                </Typography>
            </Stack>
        );
    };

    return (
        <Box
            bgcolor="white"
            borderRadius={1}
            sx={{
                border: "1px solid rgba(145, 158, 171, 0.12)",
            }}
        >
            <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                <LightTooltip title={t('dashboard.global.brandPulse.definitions.monthly_searches')}>
                    <Stack className="card-top-header-item" direction="row" alignItems="center" spacing={1} sx={{ cursor: 'help' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <line x1="1" y1="7" x2="13" y2="7" stroke="#EE7A63" stroke-width="2" stroke-linecap="round" />
                            </svg>
                            <Typography className="card-top-header-item-label" variant="h6" color="text.primary">
                                {t("dashboard.global.brandPulse.searches")}
                            </Typography>
                        </Stack>
                        <Typography className="card-top-header-item-value" variant="h6" color="text.primary">
                            {formatNumber(monthlyData.reduce(
                                (acc: number, item: any) => acc + (item.total_searches || 0),
                                0))}
                        </Typography>
                    </Stack>
                </LightTooltip>
            </Stack>

            <Box height={200}>
                <Chart type="line" series={series} options={chartOptions} height="100%" />
            </Box>

            {renderFooter(t("dashboard.global.brandPulse.last12Months"))}

        </Box >
    );
}